// src/components/forms/shared/modal/SurchargeManager.tsx

import React, { useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { StyledTextField } from '../FormElements';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import FieldHelperText from '../FieldHelperText';
import {
  formValidators,
  FormErrors,
} from '../../../../utils/validationHelpers';
import {
  InputContainer,
  IncrementButtonLarge,
  ManagerFooter,
} from '../../../common/modal/ModalElements';

interface SurchargeManagerProps {
  currentReason?: string;
  currentAmount?: number;
  onSave: (reason: string, amount: number) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface SurchargeFormData {
  reason: string;
  amount: number;
}

const SURCHARGE_INCREMENT = 1;

const SurchargeLabelText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  '& .required': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[200]
        : theme.palette.secondary.main,
    marginLeft: '4px',
  },
}));

const ReasonTextField = styled(StyledTextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

const AmountTextField = styled(StyledTextField)({
  flex: 1,
  minWidth: '120px',
  maxWidth: '120px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.6rem',
    fontWeight: 600,
    padding: '0.5rem',
  },
  '& input:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

const SurchargeManager: React.FC<SurchargeManagerProps> = ({
  currentReason = '',
  currentAmount = 0,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const [reason, setReason] = useState<string>(currentReason);
  const [amount, setAmount] = useState<number>(currentAmount);
  const [errors, setErrors] = useState<FormErrors<SurchargeFormData>>({});

  const handleAmountIncrement = () => {
    setAmount(amount + SURCHARGE_INCREMENT);
    if (errors.amount) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.amount;
        return newErrors;
      });
    }
  };

  const handleAmountDecrement = () => {
    if (amount >= SURCHARGE_INCREMENT) {
      setAmount(Math.max(0, amount - SURCHARGE_INCREMENT));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setAmount(Math.max(0, value));
    }
    if (errors.amount) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.amount;
        return newErrors;
      });
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
    if (errors.reason) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.reason;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors<SurchargeFormData> = {};

    const reasonError = formValidators.surcharge.reason(reason);
    if (reasonError) newErrors.reason = reasonError;

    const amountError = formValidators.surcharge.amount(amount);
    if (amountError) newErrors.amount = amountError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(reason, amount);
    }
  };

  const canDecrement = amount > 0;

  return (
    <Box>
      <Box sx={{ p: 0.75 }}>
        <SurchargeLabelText id="surcharge-reason-label">
          Reason{' '}
          <span className="required" aria-hidden="true">
            *
          </span>
        </SurchargeLabelText>

        <ReasonTextField
          fullWidth
          autoFocus
          value={reason}
          onChange={handleReasonChange}
          disabled={isLoading}
          inputProps={{ 'aria-labelledby': 'surcharge-reason-label' }}
          multiline
          rows={2}
          error={Boolean(errors.reason)}
        />

        {errors.reason && (
          <Box sx={{ mt: 0.5 }}>
            <FieldHelperText error={true} helperText={errors.reason} />
          </Box>
        )}

        <SurchargeLabelText id="surcharge-amount-label" sx={{ mt: 2 }}>
          Amount (R){' '}
          <span className="required" aria-hidden="true">
            *
          </span>
        </SurchargeLabelText>

        <InputContainer sx={{ mb: 1.5 }}>
          <IncrementButtonLarge
            onClick={handleAmountDecrement}
            disabled={!canDecrement || isLoading}
            title="Decrease amount"
          >
            <RemoveIcon />
          </IncrementButtonLarge>

          <AmountTextField
            type="number"
            value={amount === 0 ? '' : amount}
            onChange={handleAmountChange}
            inputProps={{
              min: 0,
              step: 1,
              'aria-labelledby': 'surcharge-amount-label',
            }}
            size="small"
            disabled={isLoading}
            error={Boolean(errors.amount)}
          />

          <IncrementButtonLarge
            onClick={handleAmountIncrement}
            disabled={isLoading}
            title="Increase amount"
          >
            <AddIcon />
          </IncrementButtonLarge>
        </InputContainer>

        {errors.amount && (
          <Box sx={{ mt: 0.5 }}>
            <FieldHelperText error={true} helperText={errors.amount} />
          </Box>
        )}
      </Box>

      <ManagerFooter sx={{ justifyContent: 'flex-end', mt: 1, pt: 1.5 }}>
        <ModalCancelButton onClick={onCancel} disabled={isLoading} />
        <ModalSaveButton onClick={handleSave} disabled={isLoading} />
      </ManagerFooter>
    </Box>
  );
};

export default SurchargeManager;
